'use client';

import { contentImageAlt } from '@communio/content-image-alt';

import Link from 'next/link';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { LoginLink } from '@/platform/auth/LoginLink';
import { useAuth } from '@/platform/auth/useAuth';
import {
  cartItemCount,
  formatOrderPrice,
  listMyOpenCarts,
  listMyOrders,
  type SellerCart,
  type VendorOrder,
} from './api';
import { MarketplaceOrderDisclaimer } from './MarketplaceOrderDisclaimer';

function formatWhen(iso?: string) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function BuyerOrdersPage() {
  const { isAuthenticated } = useAuth();
  const searchParams = useSearchParams();
  const checkoutCancelled = searchParams.get('checkout') === 'cancelled';
  const [orders, setOrders] = useState<VendorOrder[]>([]);
  const [openCarts, setOpenCarts] = useState<SellerCart[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [showCancelledNotice, setShowCancelledNotice] = useState(checkoutCancelled);

  useEffect(() => {
    setShowCancelledNotice(checkoutCancelled);
  }, [checkoutCancelled]);

  const load = useCallback((cursor?: string) => {
    const isMore = !!cursor;
    if (isMore) setLoadingMore(true);
    else setLoading(true);
    return listMyOrders(cursor ? { cursor } : undefined)
      .then((r) => {
        setOrders((prev) => (isMore ? [...prev, ...r.orders] : r.orders));
        setNextCursor(r.nextCursor);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    void load();
    void listMyOpenCarts()
      .then((r) => {
        setOpenCarts(
          (r.carts ?? []).map((order) => ({
            orderId: order.id,
            sellerUserId: order.sellerUserId,
            status: 'pending_payment' as const,
            lineItems: order.lineItems,
            subtotalCents: order.subtotalCents,
            currency: order.currency,
          })),
        );
      })
      .catch(() => setOpenCarts([]));
  }, [isAuthenticated, load]);

  if (!isAuthenticated) {
    return (
      <section className="buyer-orders feed-card settings-page">
        <p>Sign in to view your marketplace order history.</p>
        <p className="text-muted">
          If you just checked out as a guest, use the order link from the checkout return page or your
          receipt email.
        </p>
        <p>
          <LoginLink className="btn btn-primary" returnTo="/marketplace/orders/">
            Sign in
          </LoginLink>
        </p>
        <p>
          <Link href="/marketplace/">Continue shopping</Link>
        </p>
      </section>
    );
  }

  if (loading) return <p>Loading orders…</p>;
  if (error) return <p className="form-error">{error}</p>;

  return (
    <section className="buyer-orders feed-card settings-page">
      {showCancelledNotice && (
        <div className="buyer-orders__cancel-notice" role="status">
          <p>Your checkout was cancelled. No charge was made.</p>
          <p>
            <Link href="/marketplace/">Continue shopping</Link>
            {' · '}
            <button type="button" className="btn btn-link" onClick={() => setShowCancelledNotice(false)}>
              Dismiss
            </button>
          </p>
        </div>
      )}
      <MarketplaceOrderDisclaimer />
      {openCarts.length > 0 && (
        <>
          <h2 className="buyer-orders__section">Open carts</h2>
          <ul className="buyer-orders__list buyer-orders__list--carts">
            {openCarts.map((cart) => (
              <li key={cart.orderId} className="buyer-orders__item">
                <Link
                  href={`/marketplace/sellers/${cart.sellerUserId}/?cart=1`}
                  className="buyer-orders__link"
                >
                  {cart.lineItems[0]?.imageUrl && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={cart.lineItems[0].imageUrl} alt={contentImageAlt(cart.lineItems[0].name ?? 'Cart item', 'product photo')} className="order-line__thumb" loading="lazy" />
                  )}
                  <strong>Seller cart</strong>
                  <span className="buyer-orders__meta">
                    {cartItemCount(cart)} item{cartItemCount(cart) === 1 ? '' : 's'} ·{' '}
                    {formatOrderPrice(cart.subtotalCents, cart.currency)}
                  </span>
                  <span className="text-muted buyer-orders__date">Continue checkout</span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
      <h2 className="buyer-orders__section">Order history</h2>
      {orders.length === 0 ? (
        <p className="text-muted">No marketplace orders yet.</p>
      ) : (
        <ul className="buyer-orders__list">
          {orders.map((order) => (
            <li key={order.id} className="buyer-orders__item">
              <Link href={`/marketplace/orders/${order.id}/`} className="buyer-orders__link">
                {order.lineItems[0]?.imageUrl && (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img src={order.lineItems[0].imageUrl} alt={contentImageAlt(order.lineItems[0].name ?? 'Order', 'product photo')} className="order-line__thumb" loading="lazy" />
                )}
                <strong>{order.lineItems[0]?.name ?? 'Order'}</strong>
                <span className="buyer-orders__meta">
                  {formatOrderPrice(order.totalCents, order.currency)} ·{' '}
                  <span className={`buyer-orders__status buyer-orders__status--${order.status}`}>
                    {order.status.replace(/_/g, ' ')}
                  </span>
                </span>
                {formatWhen(order.createdAt) && (
                  <span className="text-muted buyer-orders__date">Placed {formatWhen(order.createdAt)}</span>
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
      {nextCursor && (
        <p>
          <button type="button" className="btn btn-ghost" disabled={loadingMore} onClick={() => void load(nextCursor)}>
            {loadingMore ? 'Loading…' : 'Load more orders'}
          </button>
        </p>
      )}
    </section>
  );
}
